import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Camera, Upload, FileImage, FileText, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface UploadAreaProps {
  actionStyle?: "default" | "prominent";
  allowPdf?: boolean;
  cameraLabel?: string;
  className?: string;
  helperText?: string;
  onFileSelect?: (file: File) => void;
  onPreviewChange?: (previewUrl: string | null) => void;
  onUploadIntent?: (method: "camera" | "drag_drop" | "image" | "pdf") => void;
  previewUrl?: string | null;
  selectedFileType?: "image" | "pdf" | null;
  uploadLabel?: string;
}

const UploadArea = ({
  actionStyle = "default",
  allowPdf = false,
  cameraLabel = "Camera",
  className,
  helperText = "Take a photo, select an existing image, or drag & drop your citation here",
  onFileSelect,
  onPreviewChange,
  onUploadIntent,
  previewUrl,
  selectedFileType = null,
  uploadLabel = "Upload",
}: UploadAreaProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const preview = previewUrl ?? localPreview;
  const isPdfSelected = selectedFileType === "pdf" && !preview;
  const useProminentActions = actionStyle === "prominent";
  const fileAccept = allowPdf ? "image/*,application/pdf" : "image/*";

  const setPreview = useCallback((value: string | null) => {
    setLocalPreview(value);
    onPreviewChange?.(value);
  }, [onPreviewChange]);

  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) {
      setPreview(null);
      onFileSelect?.(file);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);
    onFileSelect?.(file);
  }, [onFileSelect, setPreview]);

  const stopCameraStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => () => stopCameraStream(), [stopCameraStream]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      onUploadIntent?.(file.type === "application/pdf" ? "pdf" : "drag_drop");
      handleFile(file);
    }
  }, [handleFile, onUploadIntent]);

  const onInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const openUploadPicker = useCallback(() => {
    onUploadIntent?.("image");
    fileInputRef.current?.click();
  }, [onUploadIntent]);

  const openPdfPicker = useCallback(() => {
    onUploadIntent?.("pdf");
    fileInputRef.current?.click();
  }, [onUploadIntent]);

  const openCamera = useCallback(async () => {
    onUploadIntent?.("camera");
    const isTouchDevice =
      typeof navigator !== "undefined" &&
      (navigator.maxTouchPoints > 0 || /android|iphone|ipad|ipod/i.test(navigator.userAgent));

    if (isTouchDevice || !navigator.mediaDevices?.getUserMedia) {
      cameraInputRef.current?.click();
      return;
    }

    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false
      });
      streamRef.current = stream;
      setCameraOpen(true);
      window.setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play();
        }
      }, 0);
    } catch {
      setCameraError("Camera access was not available. Choose Upload to select a saved photo.");
      cameraInputRef.current?.click();
    }
  }, [onUploadIntent]);

  const closeCamera = useCallback(() => {
    stopCameraStream();
    setCameraOpen(false);
  }, [stopCameraStream]);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setCameraError("Camera is not ready yet. Try again in a moment.");
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) {
      setCameraError("Could not capture a photo from the camera.");
      return;
    }
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) {
        setCameraError("Could not save the captured photo.");
        return;
      }
      const file = new File([blob], `citation-${Date.now()}.jpg`, { type: "image/jpeg" });
      handleFile(file);
      closeCamera();
    }, "image/jpeg", 0.92);
  }, [closeCamera, handleFile]);

  return (
    <>
      <input
        ref={fileInputRef}
        accept={fileAccept}
        className="hidden"
        onChange={onInputChange}
        type="file"
      />
      <input
        ref={cameraInputRef}
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onInputChange}
        type="file"
      />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        className={cn(
          "relative rounded-[24px] border-2 border-dashed transition-all duration-300 overflow-hidden",
          isDragOver
            ? "border-primary bg-primary/5"
            : "border-border hover:border-primary/40 hover:bg-primary/[0.02]",
          preview || isPdfSelected ? "p-0" : "p-4 sm:p-12",
          className
        )}
      >
        {preview ? (
          <div className="relative">
            <img src={preview} alt="Citation preview" className="w-full rounded-[22px]" />
            <div className="absolute bottom-4 left-4 right-4 flex justify-center gap-2">
              <button
                className="inline-flex items-center gap-2 rounded-full bg-foreground/80 px-4 py-2 text-xs font-semibold text-background backdrop-blur-xl"
                onClick={openCamera}
                type="button"
              >
                <Camera size={14} /> {cameraLabel}
              </button>
              <button
                className="inline-flex items-center gap-2 rounded-full bg-background/90 px-4 py-2 text-xs font-semibold text-foreground backdrop-blur-xl"
                onClick={openUploadPicker}
                type="button"
              >
                <FileImage size={14} /> {uploadLabel}
              </button>
              {allowPdf ? (
                <button
                  className="inline-flex items-center gap-2 rounded-full bg-background/90 px-4 py-2 text-xs font-semibold text-foreground backdrop-blur-xl"
                  onClick={openPdfPicker}
                  type="button"
                >
                  <FileText size={14} /> Choose PDF
                </button>
              ) : null}
            </div>
          </div>
        ) : isPdfSelected ? (
          <div className="relative flex min-h-[260px] flex-col items-center justify-center gap-4 rounded-[22px] bg-slate-50 px-5 py-10 text-center">
            <div className="grid h-16 w-16 place-items-center rounded-2xl bg-primary/10 text-primary">
              <FileText className="h-8 w-8" />
            </div>
            <div>
              <p className="text-lg font-black text-foreground">Citation PDF selected</p>
              <p className="mx-auto mt-2 max-w-[260px] text-sm leading-6 text-muted-foreground">
                We'll scan the document and extract the citation details for your review.
              </p>
            </div>
            <div className="flex flex-col items-stretch gap-2 sm:flex-row">
              <button
                className="inline-flex items-center justify-center gap-2 rounded-full bg-foreground/85 px-4 py-2 text-xs font-semibold text-background backdrop-blur-xl"
                onClick={openCamera}
                type="button"
              >
                <Camera size={14} /> {cameraLabel}
              </button>
              <button
                className="inline-flex items-center justify-center gap-2 rounded-full bg-background px-4 py-2 text-xs font-semibold text-foreground ring-1 ring-border backdrop-blur-xl"
                onClick={openUploadPicker}
                type="button"
              >
                <FileImage size={14} /> {uploadLabel}
              </button>
              {allowPdf ? (
                <button
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-background px-4 py-2 text-xs font-semibold text-foreground ring-1 ring-border backdrop-blur-xl"
                  onClick={openPdfPicker}
                  type="button"
                >
                  <FileText size={14} /> Choose PDF
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex w-full max-w-full flex-col items-center gap-4 text-center sm:max-w-[320px] sm:gap-5">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl border-2 border-black bg-[#ffd400] sm:h-20 sm:w-20">
              <Camera size={24} strokeWidth={1.5} className="text-primary sm:h-7 sm:w-7" />
            </div>
            <div>
              <p className="text-base font-semibold text-foreground mb-1">
                Upload Your Traffic Citation
              </p>
              <p className="mx-auto max-w-[220px] text-sm text-muted-foreground sm:max-w-[240px]">
                {helperText}
              </p>
            </div>
            <div className="mt-2 flex w-full max-w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
              <button
                className={cn(
                  "inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-center text-xs font-medium leading-tight transition-colors [overflow-wrap:anywhere] sm:w-auto",
                  useProminentActions
                    ? "bg-[#ffd400] py-3 text-black ring-2 ring-black hover:bg-[#ffe45c] sm:bg-[#ffd400] sm:py-3 sm:text-black sm:hover:bg-[#ffe45c]"
                    : "bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary",
                )}
                onClick={openCamera}
                type="button"
              >
                <Camera className="shrink-0" size={12} /> <span className="min-w-0 [overflow-wrap:anywhere]">{cameraLabel}</span>
              </button>
              <button
                className={cn(
                  "inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-center text-xs font-medium leading-tight transition-colors [overflow-wrap:anywhere] sm:w-auto",
                  useProminentActions
                    ? "border-2 border-black bg-white py-3 text-black hover:bg-[#ffd400] sm:border-2 sm:bg-white sm:py-3 sm:text-black sm:hover:bg-[#ffd400]"
                    : "bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary",
                )}
                onClick={openUploadPicker}
                type="button"
              >
                <Upload className="shrink-0" size={12} /> <span className="min-w-0 [overflow-wrap:anywhere]">{uploadLabel}</span>
              </button>
              {allowPdf && !useProminentActions ? (
                <button
                  className={cn(
                    "inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-center text-xs font-medium leading-tight transition-colors [overflow-wrap:anywhere] sm:w-auto",
                    useProminentActions
                      ? "border-2 border-black bg-white py-3 text-black hover:bg-[#ffd400] sm:border-2 sm:bg-white sm:py-3 sm:text-black sm:hover:bg-[#ffd400]"
                      : "bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary",
                  )}
                  onClick={openPdfPicker}
                  type="button"
                >
                  <FileText className="shrink-0" size={12} /> <span className="min-w-0 [overflow-wrap:anywhere]">Choose PDF</span>
                </button>
              ) : null}
            </div>
            {cameraError ? <p className="text-xs font-medium text-destructive">{cameraError}</p> : null}
          </div>
        )}
      </motion.div>

      {cameraOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="w-full max-w-3xl overflow-hidden rounded-3xl bg-background shadow-2xl">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <p className="text-sm font-semibold text-foreground">Camera</p>
              <button className="rounded-full p-2 text-muted-foreground hover:bg-muted" onClick={closeCamera} type="button">
                <X size={18} />
              </button>
            </div>
            <video ref={videoRef} className="max-h-[70vh] w-full bg-black object-contain" playsInline muted />
            {cameraError ? <p className="px-4 pt-3 text-sm font-medium text-destructive">{cameraError}</p> : null}
            <div className="flex gap-3 p-4">
              <button className="flex-1 rounded-2xl border border-border px-4 py-3 text-sm font-semibold" onClick={closeCamera} type="button">
                Cancel
              </button>
              <button className="flex-1 rounded-2xl bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground" onClick={capturePhoto} type="button">
                Take Photo
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default UploadArea;
